import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, sanitizeImageUrl } from '../utils/api';
import LoadingSpinner from './LoadingSpinner';

export default function ClanInfo({ t, slug }) {
  const { data: clan, isError: error, isLoading } = useQuery({
    queryKey: ['clan_info', slug],
    queryFn: () => apiGet(`/v1/clan/${slug}/info`),
    enabled: !!slug,
    refetchInterval: 300_000, // 5 min - war stats and member counts change
  });

  if (error) {
    return (
      <div id="clanPage">
        <h1 className="text-2xl md:text-3xl mb-6">🏰 <span>{t('page_clan_info')}</span></h1>
        <div id="clanInfo" className="bg-gray-800 p-4 md:p-6 rounded-xl text-center text-red-400">{t('error_loading_data')}</div>
      </div>
    );
  }

  if (!clan) {
    return (
      <div id="clanPage">
        <h1 className="text-2xl md:text-3xl mb-6">🏰 <span>{t('page_clan_info')}</span></h1>
        <div id="clanInfo" className="bg-gray-800 rounded-xl">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  const capitalLevel = clan.capital_hall_level;
  const capitalHtml = (capitalLevel !== null && capitalLevel !== undefined && capitalLevel !== '' && Number(capitalLevel) >= 1)
    ? `${t('level')} ${Number(capitalLevel)}`
    : t('not_available');

  const reqTH = clan.required_town_hall;
  const hasReqTH = reqTH !== null && reqTH !== undefined && reqTH !== '' && Number(reqTH) >= 1;

  return (
    <div id="clanPage">
      <h1 className="text-2xl md:text-3xl mb-6">🏰 <span>{t('page_clan_info')}</span></h1>

      <div id="clanInfo">
        {/* Hero Banner */}
        <div className="ci-hero">
          <div className="ci-hero-bg" aria-hidden="true" />
          <div className="ci-hero-badge-wrap">
            <img src={sanitizeImageUrl(clan.badge)} className="ci-hero-badge" alt="Clan Badge" />
            <div className="ci-hero-badge-ring" />
          </div>
          <div className="ci-hero-info">
            <h2 className="ci-hero-name">{clan.name}</h2>
            <div className="ci-hero-tag">{clan.tag}</div>
            <p className="ci-hero-desc">{clan.description || t('no_description')}</p>
          </div>
        </div>

        {/* Basic Info */}
        <div className="ci-grid-2">
          <InfoCard icon="🎖️" label={t('level')} value={clan.level} na={t('not_available')} accent="gold" />
          <InfoCard icon="👥" label={t('members')} value={clan.members} na={t('not_available')} accent="blue" />
          <InfoCard icon="🔓" label={t('type')} value={clan.type} na={t('not_available')} accent="purple" />
          <InfoCard icon="🌍" label={t('location')} value={clan.location} na={t('not_available')} accent="teal" />
          <InfoCard icon="🏆" label={t('war_league')} value={clan.war_league} na={t('not_available')} accent="gold" />
          <InfoCard icon="🏛️" label={t('capital_hall_level')} value={capitalHtml} na={t('not_available')} accent="purple" />
        </div>

        {/* Requirements */}
        <div className="ci-section">
          <h3 className="ci-section-title">🎯 {t('requirements')}</h3>
          <div className="ci-req-blocks">
            <div className="ci-req-block">
              <span className="ci-req-block-icon">🏆</span>
              <div className="ci-req-block-label">{t('min_trophies')}</div>
              <div className="ci-req-block-value">{clan.required_trophies || t('not_available')}</div>
            </div>
            <div className="ci-req-block">
              <span className="ci-req-block-icon">🔨</span>
              <div className="ci-req-block-label">{t('min_builder_trophies')}</div>
              <div className="ci-req-block-value">{clan.required_builder_trophies || t('not_available')}</div>
            </div>
            <div className="ci-req-block">
              <div className="ci-req-block-label">{t('min_townhall')}</div>
              <div className="ci-req-block-value ci-req-block-th">
                {hasReqTH
                  ? <img src={`${process.env.PUBLIC_URL}/static/img/townhall/TH${Number(reqTH)}.webp`} className="w-10 h-10" alt={`TH${Number(reqTH)}`} />
                  : t('not_available')}
              </div>
            </div>
          </div>
        </div>

        {/* War Stats */}
        <div className="ci-section">
          <h3 className="ci-section-title">⚔️ {t('war_stats')}</h3>
          <div className="ci-war-stats-grid">
            <StatCard icon="✅" label={t('wins')} value={clan.wars_won} accent="emerald" na={t('not_available')} />
            <StatCard icon="❌" label={t('losses')} value={clan.wars_loss} accent="red" na={t('not_available')} />
            <StatCard icon="📈" label={t('win_rate')} value={clan.win_percentage != null ? `${clan.win_percentage}%` : null} accent="blue" na={t('not_available')} />
            <StatCard icon="🔥" label={t('win_streak')} value={clan.current_win_streak} accent="gold" na={t('not_available')} />
          </div>
        </div>

        {/* Additional */}
        <div className="ci-grid-2">
          <InfoCard icon="⚔️" label={t('war_frequency')} value={clan.war_frequency} accent="blue" />
          <InfoCard icon="💬" label={t('chat_language')} value={clan.chat_language} accent="teal" />
          <div className="ci-leader-card">
            <span className="ci-leader-icon">👑</span>
            <div>
              <div className="ci-req-label">{t('leader')}</div>
              <div className="ci-leader-name">{clan.leader_name || t('not_available')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const ACCENT_COLORS = {
  gold:    { bg: 'rgba(251,191,36,0.08)',  border: 'rgba(251,191,36,0.2)',  text: '#fbbf24' },
  blue:    { bg: 'rgba(96,165,250,0.08)',  border: 'rgba(96,165,250,0.2)',  text: '#60a5fa' },
  purple:  { bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.2)', text: '#a78bfa' },
  teal:    { bg: 'rgba(45,212,191,0.08)',  border: 'rgba(45,212,191,0.2)',  text: '#2dd4bf' },
  emerald: { bg: 'rgba(52,211,153,0.08)',  border: 'rgba(52,211,153,0.2)',  text: '#34d399' },
  red:     { bg: 'rgba(248,113,113,0.08)', border: 'rgba(248,113,113,0.2)', text: '#f87171' },
};

function InfoCard({ icon, label, value, na, accent = 'blue' }) {
  const display = (value !== null && value !== undefined && value !== '') ? value : (na || '-');
  const c = ACCENT_COLORS[accent] || ACCENT_COLORS.blue;
  return (
    <div className="ci-info-card" style={{ background: c.bg, borderColor: c.border }}>
      <div className="ci-card-inline">
        <span className="ci-card-icon" style={{ color: c.text }}>{icon}</span>
        <span className="ci-card-label">{label}</span>
      </div>
      <div className="ci-card-value">{display}</div>
    </div>
  );
}

function StatCard({ icon, label, value, accent = 'blue', na }) {
  const display = (value !== null && value !== undefined && value !== '') ? value : (na || '-');
  const c = ACCENT_COLORS[accent] || ACCENT_COLORS.blue;
  return (
    <div className="ci-stat-card" style={{ borderColor: c.border }}>
      <span className="ci-stat-icon">{icon}</span>
      <div className="ci-stat-label">{label}</div>
      <div className="ci-stat-value" style={{ color: c.text }}>{display}</div>
    </div>
  );
}
